import {
  Component,
  ComponentFactoryResolver,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { DatePickerInnerComponent } from './datepicker-inner.component';
import { DatepickerConfig } from './datepicker.config';
import { DatepickerService } from './datepicker.shared.service';

/* tslint:disable:component-selector-name component-selector-type */
@Component({
  selector: 'axis-datepicker-container, [axis-datepicker-container]',
  host: {
    class: 'axis-datepicker'
  },
  template: `
    <axis-datepicker-inner #innerref [activeDate]="activeDate"
                      (update)="onUpdate($event)"
                      [datepickerMode]="datepickerMode"
                      [initDate]="initDate"
                      [disableDays]="disableDays"
                      [disableDates]="disableDates"
                      [isTodayVisiable]="isTodayVisiable"
                      [isTodayVisible]="isTodayVisible"
                      [isClearVisible]="isClearVisible"
                      [minDate]="minDate"
                      [maxDate]="maxDate"
                      [minMode]="minMode"
                      [maxMode]="maxMode"
                      [showWeeks]="showWeeks"
                      [formatDay]="formatDay"
                      [formatMonth]="formatMonth"
                      [formatYear]="formatYear"
                      [formatDayHeader]="formatDayHeader"
                      [formatDayTitle]="formatDayTitle"
                      [formatMonthTitle]="formatMonthTitle"
                      [startingDay]="startingDay"
                      [yearRange]="yearRange"
                      [customClass]="customClass"
                      [dateDisabled]="dateDisabled"
                      [onlyCurrentMonth]="onlyCurrentMonth"
                      [shortcutPropagation]="shortcutPropagation"
                      [monthColLimit]="monthColLimit"
                      [yearColLimit]="yearColLimit"
                      [selectOnDay]="selectOnDay"
                      [selectOnMonth]="selectOnMonth"
                      [selectOnYear]="selectOnYear"
                      [isReadOnly]="isReadOnly"
                      [ariaLabel]="ariaLabel"
                      [ariaLabelledby]="ariaLabelledby"
                      (selectionDone)="onSelectionDone($event)"
                      (closeEvent)="oncloseEvent($event)">
    </axis-datepicker-inner>
    `
})
/* tslint:enable:component-selector-name component-selector-type */
export class DatePickerComponent implements OnInit {
  /** currently active date */
  @Input()
  public get activeDate(): Date {
    return this._activeDate || this._now;
  }

  public set activeDate(value: Date) {
    this._activeDate = value;
  }
  /** array of custom css classes to be applied to targeted dates */
  @Input() public customClass: { date: Date; mode: string; clazz: string }[];
  @Input() public clickedInside: any;
  @Input() public disableDays: number[] = [];
  @Input() public disableDates = [];
  /** sets datepicker mode, supports: `day`, `month`, `year` */
  @Input() public dateDisabled: { date: Date; mode: string }[];
  @Input() public datepickerMode = 'day';
  @Input() public isTodayVisiable = true;
  @Input() public isTodayVisible = true;
  @Input() public isClearVisible;
  /** default date to show if `ng-model` value is not specified */
  @Input() public initDate: Date;
  /** format of day in month */
  @Input() public formatDay: string;
  /** format of month in year */
  @Input() public formatMonth: string;
  /** format of year in year range */
  @Input() public formatYear: string;
  /** format of day in week header */
  @Input() public formatDayHeader: string;
  /** format of title when selecting day */
  @Input() public formatDayTitle: string;
  /** format of title when selecting month */
  @Input() public formatMonthTitle: string;
  /**  oldest selectable date */
  @Input() public minDate: Date;
  /** latest selectable date */
  @Input() public maxDate: Date;
  /** number of months displayed in a single row of month picker */
  @Input() public monthColLimit: number;
  /** set lower datepicker mode, supports: `day`, `month`, `year` */
  @Input() public minMode: string;
  /** sets upper datepicker mode, supports: `day`, `month`, `year` */
  @Input() public maxMode: string;
  /** if false week numbers will be hidden */
  /** if true only dates from the currently displayed month will be shown */
  @Input() public onlyCurrentMonth: boolean;
  @Input() public showWeeks = false;
  /** if true shortcut`s event propagation will be disabled */
  @Input() public shortcutPropagation: boolean;
  /** starting day of the week from 0-6 (0=Sunday, ..., 6=Saturday) */
  @Input() public startingDay: number;
  /** array of disabled dates */
  @Input() selectOnDay = false;
  @Input() selectOnMonth = false;
  @Input() selectOnYear = false;

  /** number of years displayed in a single row of year picker */
  @Input() public yearColLimit: number;
  /** number of years displayed in year selection */
  @Input() public yearRange: number;
  @Input() public isReadOnly = true;

  @Input() ariaLabel;
  @Input() ariaLabelledby;



  @Output() public clickOutside = new EventEmitter<MouseEvent>();
  @Output()
  public closeEvent: EventEmitter<Date> = new EventEmitter<Date>(undefined);
  @Output()
  public selectionDone: EventEmitter<Date> = new EventEmitter<Date>(undefined);
  @ViewChild(DatePickerInnerComponent, { static: true })
  public _datePicker: DatePickerInnerComponent;
  @ViewChild('innerref', { static: true }) innerref: DatePickerInnerComponent;

  public onChange: any = Function.prototype;
  public onTouched: any = Function.prototype;

  public modelValue: Date;

  protected _now: Date = new Date();
  protected _activeDate: Date;
  protected config: DatepickerConfig;
  // @HostBinding('class.axis-datepicker-container')
  // AxisDatepickerContainer = true;
  public constructor(
    config: DatepickerConfig,
    private viewContainerRef: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    private datepickerService: DatepickerService,
    private el: ElementRef
  ) {
    this.config = config;
    this.configureOptions();
    this.datepickerService.modelUpdateObservable.subscribe(
      (modelValue: Date) => {
        this.modelValue = modelValue;
        this.proxywriteValue(this.modelValue);
      }
    );
  }

  public configureOptions(): void {
    Object.assign(
      this,
      this.config,
      this.datepickerService.getDatepickerInstance()
    );
  }

  ngOnInit() {
    if (this.modelValue && !isNaN(this.modelValue.valueOf())) {
      this.proxywriteValue(this.modelValue);
    }
  }
  public onUpdate(event: any): void {
    this.activeDate = event;
    this.modelValue = event;
  }

  public onSelectionDone(event: Date): void {
    this.selectionDone.emit(event);
    this.onUpdate(event);
  }

  public oncloseEvent(event: Date): void {
    this.closeEvent.emit(event);
  }

  // todo: support null value
  public proxywriteValue(value: any): void {
    if (this._datePicker.compare(value, this._activeDate) === 0) {
      return;
    }
    if (value && value instanceof Date && this['isValid']) {
      this.activeDate = value;
      this._datePicker.select(value, false);
      return;
    }
    this.activeDate = value && this['isValid'] ? new Date(value) : void 0;
  }
}
